import { Component, OnChanges, OnInit, SimpleChanges, inject, ChangeDetectionStrategy, signal, input, output } from '@angular/core';
import {GuruEntity} from '../../../model/guru-entity';
import {EntityLine} from '../../../entity/entity-line';
import {EntitySureFlowUnit} from '../../../model/entity-sure-flow-unit';
import {TagInformation} from '../../../model/tag-information';
import {DialogService} from '../../../services/dialog.service';
import {SingleEntityLine} from '../../../entity/single-entity-line';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';


@Component({
    selector: 'app-unit-details-section',
    templateUrl: './unit-details-section.component.html',
    styleUrls: ['./unit-details-section.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatIcon, MatButton, FormsModule]
})
export class UnitDetailsSectionComponent implements OnChanges {
  private readonly dialogService = inject(DialogService);


  readonly selections = input.required<GuruEntity[]>();
  readonly invalidEntityNames = input.required<string[]>();
  readonly entityLines = input.required<EntityLine[]>();
  readonly selectedUnits = input.required<EntitySureFlowUnit[]>();
  readonly selectionChanged = output<GuruEntity>();
  readonly tagsUpdated = output<TagInformation[]>();
  packagingSelection!: GuruEntity;
  collapsed: boolean = false;
  canExpand: boolean = true;
  readonly displayTags = signal<string>(undefined!);
  displayUnitCount!: string;
  readonly tagInformation = signal<TagInformation[]>(undefined!);
  private readonly selectionNames: string[] = ['USPackaging'];

  private updateDisplayTags(): void {
    this.displayTags.set(this.selectedUnits().map(unit => unit.unitData.userTag).join(','));
  }

  private getUnitSingleEntityLine(unit: EntitySureFlowUnit): SingleEntityLine {
    const filteredLines = this.entityLines().filter(line => line.lineId === unit.entityLineId);
    if (filteredLines.length === 1) {
      const entityLine: EntityLine = filteredLines[0];
      const filteredSels: SingleEntityLine[] = entityLine.singleEntityLines.filter(sel => sel.sequence === unit.sequence);
      if (filteredSels.length === 1) {
        return filteredSels[0];
      }
    }
    return undefined!;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedUnits']) {
      this.tagInformation.set([]);
      this.displayUnitCount = (this.selectedUnits()?.length || 0).toString();
      if ((this.selectedUnits()?.length || 0) > 0) {
        this.updateDisplayTags();
        for (const unit of this.selectedUnits()) {
          const tag: TagInformation = new TagInformation();
          tag.sequence = unit.sequence;
          tag.tag = unit.unitData.userTag;
          const singleEntityLine: SingleEntityLine = this.getUnitSingleEntityLine(unit);
          if (singleEntityLine) {
            tag.tag = singleEntityLine.tag;
            tag.building = singleEntityLine.building;
            tag.floor = singleEntityLine.fl;
            tag.poLine = singleEntityLine.poline;
            tag.riser = singleEntityLine.riser;
            tag.room = singleEntityLine.room;
          }
          this.tagInformation.update(current => [...current, tag]);
        }
        this.tagsUpdated.emit(this.tagInformation());
      }
    }
    if (changes['selections']) {
      const filteredSelections: GuruEntity[] = this.selections().filter(selection => this.selectionNames.indexOf(selection.Name) >= 0);
      if (filteredSelections.length >= 0) {
        this.packagingSelection = this.getSelectionWithName(this.selectionNames[0], filteredSelections);
      }
    }
  }

  private getSelectionWithName(name: string, selections: GuruEntity[]): GuruEntity {
    for (const selection of selections) {
      if (selection.Name === name) {
        return JSON.parse(JSON.stringify(selection));
      }
    }
    return undefined!;
  }

  selectedValueChanged(selectionName: string, value: string): void {
    const entity: GuruEntity = new GuruEntity();
    entity.Name = selectionName;
    entity.Value = value;
    this.selectionChanged.emit(entity);
  }

  getExpansionIcon(): string {
    return this.collapsed ? 'arrow_drop_down' : 'arrow_drop_up';
  }

  toggleExpansion(): void {
    if (this.canExpand) {
      this.collapsed = !this.collapsed;
    }
  }

  isInvalidSelection(selectionName: string): boolean {
    return this.invalidEntityNames().indexOf(selectionName) >= 0;
  }

  openTagsDialog(): void {
    const clonedTags = JSON.parse(JSON.stringify(this.tagInformation()));
    this.dialogService.showTagManagementDialog(clonedTags, 460, this.selectedUnits().length, true).subscribe({
      next: (results: TagInformation[]) => {
        if ((results?.length || 0) > 0) {
          for (const result of results) {
            const unit = this.selectedUnits().filter(unit => unit.sequence === result.sequence)[0];
            unit.unitData.userTag = result.tag;
          }
          this.tagInformation.set(results);
          this.updateDisplayTags();
          this.tagsUpdated.emit(results);
        }
      }
    });
  }

}
